import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ImageFns, ProxImage } from '@utils';
import * as _ from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface FileGalleryDialogData {
  newImages: ProxImage[];
  oldImages: ProxImage[];
  /** callback when an image is deleted */
  onDelete: (image: ProxImage) => void;
  /** callback when an image is added or changed.  This function is responsible for turning image structures into entities. */
  onChange: (image: ProxImage) => Promise<ProxImage>;
  /** enable Save button to commit changes (if false, parent is responsible for saving) */
  enableSave?: boolean;
}

@Component({
  selector: 'prox-file-gallery-dialog',
  templateUrl: './file-gallery-dialog.component.html',
  standalone: false
})
export class FileGalleryDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('gal_file') filePicker!: ElementRef<HTMLInputElement>;
  static config: DynamicDialogConfig = { header: 'View Images', width: '80vw', height: '80vh', closable: true };
  activeIndex = 0;
  showGallery = true;
  showEdit = false;
  images: ProxImage[] = [];
  cropInput?: File;
  data: FileGalleryDialogData;
  canDelete = false;

  constructor(dialogConfig: DynamicDialogConfig, private ref: DynamicDialogRef, private readonly sanitizer: DomSanitizer) {
    this.data = dialogConfig.data;
    this.prepare();
  }

  prepare(): void {
    this.activeIndex = 0;
    const images = ImageFns.convertFileData(this.data.oldImages)
    this.images = images.concat(this.data.newImages);
    this.images.sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) {
        return -1;
      } else if (b.isPrimary && !a.isPrimary) {
        return 1;
      }
      return a.fileName.localeCompare(b.fileName);
    });
    ImageFns.generateUrls(this.images, this.sanitizer);
    if (!this.images.length) {
      this.images.push(ImageFns.noimage);
    }
    this.canDelete = !!this.data.onDelete;
  }

  ngAfterViewInit(): void {
    if (!this.images.length || (this.images.length == 1 && this.isNoImage(this.images[0]))) {
      this.filePicker.nativeElement.click();
    }
  }

  ngOnDestroy(): void {
    ImageFns.clearUrls(this.images);
    this.images = [];
  }

  deleteImage(image: ProxImage): void {
    if (image && this.data.onDelete) {
      this.data.onDelete(image);
      if (image.blobUrl) {
        image.safeUrl = undefined;
        URL.revokeObjectURL(image.blobUrl);
      }
      _.remove(this.images, im => im === image);
      if (this.activeIndex >= this.images.length && this.activeIndex > 0) {
        this.activeIndex--;
      }
      if (!this.images.length) {
        this.images.push(ImageFns.noimage);
      }
      this.redraw();
    }
  }

  isNoImage(image: ProxImage) {
    return image && image.safeUrl == ImageFns.noimage.safeUrl;
  }

  /** return false if image is a thumbnail, or has a related thumbnail already */
  canMakeThumbnail(image: ProxImage): boolean {
    const thumbname = ImageFns.addSuffix(image.fileName, '-thumb');
    return !image.isThumbnail && !this.images.some(i => i.fileName == thumbname);
  }

  /** Create a new image as a thumbnail of the given image */
  async makeThumbnail(image: ProxImage) {
    if (!image.file) {
      return;
    }
    const thumb = await ImageFns.resizeFileToThumbnail(image.file);
    this.addImage(thumb);
  }

  /** Ensure that at most 1 image is set primary */
  async primaryChange(item: ProxImage) {
    if (this.data.onChange) {
      item = await this.data.onChange(item);
    }
    if (item.isPrimary) {
      // make all other images non-primary, but treat thumbnails separately
      for (const i of this.images) {
        const primary = (i == item);
        if (i.isPrimary != primary && i.isThumbnail == item.isThumbnail) {
          i.isPrimary = primary;
          if (this.data.onChange) {
            this.data.onChange(i);
          }
        }
      }
    }
  }

  /** Open imageCropper for active image */
  edit(item: ProxImage) {
    this.cropInput = item.file;
    this.showGallery = false;
    this.showEdit = true;
  }


  /** When Ok clicked on cropper, add the cropped file as a new image */
  async cropOk(blob: Blob) {
    if (blob) {
      const image = this.images[this.activeIndex];
      const file = new File([blob], image.fileName, { lastModified: image.file?.lastModified, type: blob.type });
      const names = this.images.map(i => i.fileName);
      const newName = ImageFns.getNextName(image.fileName, names);
      const image2 = await ImageFns.resizeFileToStandard(file, newName);
      this.addImage(image2);
    }
    this.cropCancel();
  }

  /** When Cancel clicked on cropper */
  cropCancel() {
    this.showEdit = false;
    this.showGallery = true;
  }

  close() {
    ImageFns.clearUrls(this.images);
    this.ref.close();
  }

  /** Close dialog and return the currently selected image */
  save(appImage: ProxImage) {
    if (!appImage || this.isNoImage(appImage)) {
      this.close();
      return;
    }
    const el = document.getElementById("gallery-main-image") as HTMLImageElement;
    if (el) {
      appImage.height = el.naturalHeight;
      appImage.width = el.naturalWidth;
    }

    ImageFns.clearUrls(this.images);
    this.ref.close(appImage);
  }

  /** Add to list as a new image */
  private async addImage(thumb: ProxImage) {
    if (this.data.onChange) {
      thumb = await this.data.onChange(thumb) as ProxImage;
    }
    this.data.newImages?.push(thumb);
    ImageFns.generateUrls([thumb], this.sanitizer);
    this.images.splice(this.activeIndex + 1, 0, thumb);
    this.activeIndex++;
    if (this.data.onChange) {
      this.data.onChange(thumb);
    }
    this.redraw();
  }

  /** Add files chosen by the file control to the newImages collection */
  async addFiles(event: Event) {
    const added = await this.addInputFiles(
      event,
      this.data.newImages,
    );
    if (!added || !added.length) { return; }
    if (this.images.length && this.isNoImage(this.images[0])) {
      this.images.shift();
    }
    ImageFns.generateUrls(added, this.sanitizer);
    this.activeIndex = this.images.length;
    this.images.push(...added);
    this.redraw();
  }

  /** Add files chosen by a input file control to the newImages collection */
  private async addInputFiles(event: Event, newImages: ProxImage[]) {
    if (event.target && (event.target as HTMLInputElement).files) {
      const files = (event.target as HTMLInputElement).files as FileList;
      const p: Promise<ProxImage>[] = [];
      for (let i = 0; i < files?.length; i++) {
        p[i] = ImageFns.resizeFileToStandard(files[i]).then(async (appImage) => {
          const imageEntity = await this.data.onChange(appImage);
          newImages.push(imageEntity);
          return imageEntity;
        });
      }
      return Promise.all(p) as Promise<ProxImage[]>;
    }
    return null;
  }

  /** Hide and show the gallery to reset the thumbnail display. */
  private redraw() {
    this.showGallery = false;
    setTimeout(() => {
      this.showGallery = true;
    }, 0);
  }

}
